import PropTypes from 'prop-types'
import {useFormContext, Controller} from 'react-hook-form'
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
  Divider,
  Checkbox,
} from '@mui/material'
import {BORDER} from '../../styles/foundation/colors'

RHFSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  disableOptions: PropTypes.array,
  multiSelect: PropTypes.bool,
  // Add other propTypes as necessary
}

export default function RHFSelectField({
  name,
  options = [],
  variant,
  label,
  disableOptions = [],
  size = 'small',
  width = 150,
  maxHeight = 300,
  noOptionsText = 'No options available',
  enableNoneSelect = false,
  multiSelect = false, // New prop for multi-selection
  ...other
}) {
  const {control} = useFormContext()
  const noOptionsAvailable = options.length === 0
  const uniqueId = new Date().getTime()
  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: maxHeight,
        borderRadius: '10px',
        border: `1px solid ${BORDER}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        mt: 0.5,
      },
    },
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => (
        <FormControl
          variant={variant || 'outlined'}
          error={!!error}
          size={size}
          sx={{minWidth: width}}
          fullWidth
        >
          <InputLabel id={`mui-select-${uniqueId}`} sx={{
            fontSize: 10,
          }}>
            {label}
            {other.mandatory && <span style={{color: 'red'}}> *</span>}
          </InputLabel>
          <Select
            {...field}
            {...(variant === 'outlined' && {
              input: <OutlinedInput label={label} size={size} />,
            })}
            multiple={multiSelect} // Enable multi-select based on the prop
            labelId={`mui-select-${uniqueId}`}
            size={size}
            MenuProps={menuProps}
            sx={{ fontSize: 12 }}
            value={typeof field.value === 'number' && field.value === 0 ? '' : field.value}
            // renderValue={(selected) => {
            //   const selectedOption = options.find(option => option.value === selected);
            //   return selectedOption ? selectedOption.label : '';
            // }}
            renderValue={(selected) => {
              const labelFor = (val) => options.find((option) => option.value === val)?.label ?? ''
              const text = Array.isArray(selected)
                ? selected.map(labelFor).filter(Boolean).join(', ')
                : labelFor(selected)
              return (
                <Typography
                  sx={{
                    fontSize: 12,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {text}
                </Typography>
              )
            }}
            {...other}
            error={!!error}
          >
            {enableNoneSelect && !multiSelect && (
              <MenuItem value=''>
                <em>All</em>
              </MenuItem>
            )}

            {noOptionsAvailable ? (
              <MenuItem disabled value=''>
                <em>{noOptionsText}</em>
              </MenuItem>
            ) : (
              options.map((option, index) => ([
                index > 0 && <Divider key={`divider-${index}`} sx={{my: 0}} />,
                <MenuItem
                  disabled={disableOptions.includes(option.value)}
                  key={index}
                  value={option.value}
                  sx={{fontSize: 12, py: 0.5, pl: multiSelect ? 0.5 : 1.5}}
                >
                  {multiSelect && (
                    <Checkbox
                      size="small"
                      checked={Array.isArray(field.value) && field.value.includes(option.value)}
                      sx={{p: 0.5, mr: 0.5}}
                    />
                  )}
                  {option.label}
                </MenuItem>,
              ]))
            )}
          </Select>
          {error?.message && <FormHelperText sx={{fontSize: 12}}>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}
